import moment from 'moment';

class TimeRange {

    formatHour = 'HH';
    formatMinute = 'mm';
    formatSecond = 'ss';
    formatDayHour = 'DD[T-]HH[h]';
    formatHourMinute = 'HH:mm';
    formatMonthYear = 'MM.YY';
    formatMonthFullYear = 'MM.YYYY';
    formatDayMonthYear = 'DD.MM.YY';
    formatFullYear = 'YYYY';
    formatDayMonthFullYear = 'DD.MM.YYYY';
    formatFullDate = 'DD.MM.YY HH:mm';
    formatFullDateTime = 'DD.MM.YY HH:mm:ss';

    dateType = 'current';   // 'current','block','range' or null
    current = null;         // only when dateType 'current': 'year','month','quarter',null
    blockYear = null;       // only when dateType 'block': 2016,2017 or null
    blockQuarter = null;    // only when dateType 'block': 1,2,3,4 or null
    blockMonth = null;      // only when dateType 'block': 1,2...12 or null
    rangeFrom = null;       // only when dateType 'range': YYYY-MM-DD or null
    rangeTill = null;       // only when dateType 'range': YYYY-MM-DD or null
    rangeRealTill = null;   // only when dateType 'range': YYYY-MM-DD or null

    /**
     * @param {object} formatOptions
     */
    constructor(formatOptions) {

        if (formatOptions) this.setFormats(formatOptions);

        this.setBlock = this.setBlock.bind(this);
        this.isBlock = this.isBlock.bind(this);
        this.serialize = this.serialize.bind(this);

    }

    setFormats(formatOptions) {

        if (formatOptions) {
            if (formatOptions.formatMonthYear) this.formatMonthYear = formatOptions.formatMonthYear;
            if (formatOptions.formatDayMonthYear) this.formatDayMonthYear = formatOptions.formatDayMonthYear;
            if (formatOptions.formatFullYear) this.formatFullYear = formatOptions.formatFullYear;
            if (formatOptions.formatDayMonthFullYear) this.formatDayMonthFullYear = formatOptions.formatDayMonthFullYear;
            if (formatOptions.formatFullDate) this.formatFullDate = formatOptions.formatFullDate;
            if (formatOptions.formatFullDateTime) this.formatFullDateTime = formatOptions.formatFullDateTime;
        }

    }

    serialize() {

        if (this.dateType === 'current')
            return JSON.stringify({'dateType': this.dateType, 'current': this.current});

        if (this.dateType === 'block')
            return JSON.stringify({
                'dateType': this.dateType,
                'blockYear': this.blockYear,
                'blockQuarter': this.blockQuarter,
                'blockMonth': this.blockMonth
            });

        if (this.dateType === 'range')
            return JSON.stringify({
                'dateType': this.dateType,
                'rangeFrom': this.rangeFrom,
                'rangeTill': this.rangeTill
            });

        // Fallback
        return JSON.stringify(this);

    }

    deserialize(string) {

        try {
            let ob = JSON.parse(string);

            if (ob.dateType === 'current')
                this.setCurrent(ob.current);

            if (ob.dateType === 'block')
                this.setBlock(ob.blockYear, ob.blockQuarter, ob.blockMonth);

            if (ob.dateType === 'range')
                this.setRange(moment(ob.rangeFrom), moment(ob.rangeTill));
        } catch (e) {
            // error
            return null;
        }

        return this;

    }

    setRange(from, till) {

        this.dateType = 'range';
        this.current = null;
        this.blockYear = null;
        this.blockQuarter = null;
        this.blockMonth = null;
        this.rangeFrom = from;
        this.rangeTill = till;
        this.rangeRealTill = (till !== null) ? till.clone().add(1,'days').startOf('day') : null;

        return this;

    }

    setBlock(year, quarter, month) {

        this.dateType = 'block';
        this.current = null;
        this.blockYear = year;
        this.blockQuarter = quarter;
        this.blockMonth = month;
        this.rangeFrom = null;
        this.rangeTill = null;

        return this;

    }

    /**
     * @param current 'year','month','quarter'
     */
    setCurrent(current) {

        this.dateType = 'current';
        this.current = current;
        this.blockYear = null;
        this.blockQuarter = null;
        this.blockMonth = null;
        this.rangeFrom = null;
        this.rangeTill = null;

        return this;

    }

    isCurrent(current) {

        return (this.dateType === 'current' && this.current === current);

    }

    isBlock(year, quarter, month) {

        if (this.dateType !== 'block' || this.blockYear !== year) return false;

        if (quarter == null
            && this.blockQuarter === null
            && month === null
            && this.blockMonth === null) return true;

        if (quarter && quarter === this.blockQuarter) return true;

        return (month && month === this.blockMonth);

    }

    getFromMoment() {

        if (this.dateType === 'current') {
            let now = moment();

            if (this.current === 'year')
                return moment(now.year(), this.formatFullYear);
            if (this.current === 'quarter')
                return moment(now.year() + '_' + now.quarter(), 'YYYY_Q');
            if (this.current === 'month')
                return moment(now.year() + '_' + (now.month() + 1), 'YYYY_M');
        }

        if (this.dateType === 'block') {
            if (this.blockMonth === null && this.blockQuarter === null)
                return moment(this.blockYear, this.formatFullYear);
            if (this.blockQuarter)
                return moment(this.blockYear + '_' + this.blockQuarter, 'YYYY_Q');
            if (this.blockMonth)
                return moment(this.blockYear + '_' + this.blockMonth, 'YYYY_M');
        }

        if (this.dateType === 'range') return this.rangeFrom;

    }

    getTillMoment() {

        if (this.dateType === 'current') {
            let now = moment();

            if (this.current === 'year')
                return moment(now.year() + 1, this.formatFullYear).subtract(1, 'second');

            if (this.current === 'quarter') {
                let x = moment(now.year() + '_' + now.quarter(), 'YYYY_Q').add(1, 'quarter');
                return x.subtract(1, 'seconds');
            }

            if (this.current === 'month') {
                let x = moment(now.year() + '_' + (now.month() + 1), 'YYYY_M');
                return x.add(1, 'month').subtract(1, 'seconds');
            }
        }

        if (this.dateType === 'block') {
            if (this.blockMonth === null && this.blockQuarter === null) {
                let x = moment(this.blockYear, this.formatFullYear);
                return x.add(1, 'year').subtract(1, 'seconds');
            }

            if (this.blockQuarter)
                return moment(this.blockYear + '_' + this.blockQuarter, 'YYYY_Q').add(1, 'quarter').subtract(1, 'seconds');

            if (this.blockMonth)
                return moment(this.blockYear + '_' + this.blockMonth, 'YYYY_M').add(1, 'month').subtract(1, 'seconds');
        }

        if (this.dateType === 'range') return this.rangeTill;

    }

    getDisplayGranularity() {

        if (this.dateType === 'current') {
            if (this.current === 'year')
                return {interval: 'months', unit: 'Month', translationKey: 'month', label: this.formatMonthFullYear, realDate: this.formatMonthFullYear, duration: moment.duration(1, 'months')};
            if (this.current === 'quarter')
                return {interval: 'months', unit: 'Month', translationKey: 'month', label: this.formatMonthFullYear, realDate: this.formatMonthFullYear, duration: moment.duration(1, 'months')};
                //return {interval: 'days', unit: 'Day', translationKey: 'day', label: this.formatDayMonthFullYear, realDate: this.formatDayMonthFullYear, duration: moment.duration(1, 'days')};
            if (this.current === 'month')
                return {interval: 'days', unit: 'Day', translationKey: 'day', label: this.formatDayMonthFullYear, realDate: this.formatDayMonthFullYear, duration: moment.duration(1, 'days')};
        }

        if (this.dateType === 'block') {
            if (this.blockMonth === null && this.blockQuarter === null)
                return {interval: 'months', unit: 'Month', translationKey: 'month', label: this.formatMonthFullYear, realDate: this.formatMonthFullYear, duration: moment.duration(1, 'months')};
            if (this.blockMonth === null && this.blockQuarter)
                return {interval: 'months', unit: 'Month', translationKey: 'month', label: this.formatMonthFullYear, realDate: this.formatMonthFullYear, duration: moment.duration(1, 'months')};
                //return {interval: 'days', unit: 'Day', translationKey: 'day', label: this.formatDayMonthFullYear, realDate: this.formatDayMonthFullYear, duration: moment.duration(1, 'days')};
            if (this.blockMonth && this.blockQuarter === null)
                return {interval: 'days', unit: 'Day', translationKey: 'day', label: this.formatDayMonthFullYear, realDate: this.formatDayMonthFullYear, duration: moment.duration(1, 'days')};
        }

        if (this.dateType === 'range') {
            if (this.rangeFrom && this.rangeTill) {
                //const diffYears = Math.abs(this.rangeFrom.diff(this.rangeRealTill, 'years'));
                const diffMonths = Math.abs(this.rangeFrom.diff(this.rangeRealTill, 'months'));
                const diffDays = Math.abs(this.rangeFrom.diff(this.rangeRealTill, 'days'));
                //const diffHours = Math.abs(this.rangeFrom.diff(this.rangeRealTill, 'hours'));
                const diffMinutes = Math.abs(this.rangeFrom.diff(this.rangeTill, 'minutes'));

                //  more than 1 year
                if (diffMonths > 12) return {interval: 'years', unit: 'Year', translationKey: 'year', label: this.formatFullYear, realDate: this.formatFullYear, duration: moment.duration(1, 'year')};

                //  more than 1 month
                if (diffMonths > 1) return {interval: 'months', unit: 'Month', translationKey: 'month', label: this.formatMonthFullYear, realDate: this.formatMonthFullYear, duration: moment.duration(1, 'months')};

                //  more than 1 days
                if (diffDays > 1) return {interval: 'days', unit: 'Day', translationKey: 'day', label: this.formatDayMonthFullYear, realDate: this.formatDayMonthFullYear, duration: moment.duration(1, 'days')};

                //  more than 1 day
                //if (diffDays > 1) return {interval: 'hours', unit: 'Hour', translationKey: 'hour', label: this.formatDayHour, realDate: this.formatFullDate, duration: moment.duration(1, 'hours')};

                //  more than 1 hour
                if (diffMinutes > 60) return {interval: 'hours', unit: 'Hour', translationKey: 'hour', label: this.formatHour, realDate: this.formatFullDate, duration: moment.duration(1, 'hours')};

                //  Fallback minutes
                return {interval: 'minutes', unit: 'Minute', translationKey: 'minute', label: this.formatMinute, realDate: this.formatFullDate, duration: moment.duration(1, 'minutes')};
            }
        }

        return {interval: 'months', unit: 'Month', translationKey: 'month', label: this.formatMonthFullYear, realDate: this.formatMonthFullYear, duration: moment.duration(1, 'months')};

    }

    getDisplayHeadline(formatString) {

        const gran = this.getDisplayGranularity();
        const format = (formatString) ? formatString : this.formatDayMonthFullYear;

        let timeRange = '';

        switch (gran.interval) {
            case 'years':
                timeRange = this.getFromMoment().format(this.formatFullYear) + ' - ' + this.getTillMoment().format(this.formatFullYear);
                break;

            case 'hours':
                timeRange = this.getFromMoment().format(this.formatDayMonthFullYear);
                break;

            case 'minutes':
                timeRange = this.getFromMoment().format(this.formatDayMonthFullYear) + ' ' + this.getFromMoment().format(this.formatHour) + ' h';
                break;

            default:
                if (this.getFromMoment() && this.getTillMoment()) timeRange = this.getFromMoment().format(format) + ' - ' + this.getTillMoment().format(format);
                break;
        }

        return {
            range: timeRange,
            interval: gran.interval,
            unit: gran.unit,
        };

    }

    isValid() {

        let isValid = false;

        if (this.dateType === 'current') {
            if (this.current === 'year' || this.current === 'quarter' || this.current === 'month') isValid = true;
        } else if (this.dateType === 'block') {
            if (this.blockMonth === null && this.blockQuarter === null) isValid = true;
            if (this.blockMonth === null && this.blockQuarter) isValid = true;
            if (this.blockMonth && this.blockQuarter === null) isValid = true;
        } else if (this.dateType === 'range') {
            if (this.rangeFrom && this.rangeTill) {
                if (this.rangeFrom.isValid() && this.rangeTill.isValid()) isValid = true;
            }
        }

        return isValid;

    }

    isNotInFuture() {

        const now = moment();
        const from = this.getFromMoment();
        const till = this.getTillMoment();

        let isInTheFuture = true;

        if (from && till) {
            if (from.isValid() && till.isValid()) {
                const nowFrom = now.diff(from, 'minutes');
                const nowTill = now.diff(till, 'minutes');

                if (nowFrom < 0 && nowTill < 0) isInTheFuture = false;
            }
        }

        return isInTheFuture;

    }

    toString() {

        return 'TimeRange:'
             + ' From:' + this.getFromMoment()
             + ' Till:' + this.getTillMoment()
             + ' Type:' + this.dateType + ' '
             + ((this.dateType === 'current') ? this.current : '')
             + ((this.dateType === 'block') ? ' Year:' + this.blockYear + ' Quarter:' + this.blockQuarter + ' Month:' + this.blockMonth : '')
             + ((this.dateType === 'range') ? ' From:' + this.rangeFrom + ' Till:' + this.rangeTill : '');

    }

}

export default TimeRange;